/**
 * Management Service
 * 
 * Service for handling management-related API calls (jabatan/positions).
 */

import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

export type JabatanLevel = "nasional" | "province" | "city";

export interface Jabatan {
  _id: string;
  name: string;
  description?: string;
  level: JabatanLevel;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateJabatanInput {
  name: string;
  description?: string;
  level: JabatanLevel;
  order: number;
}

export interface UpdateJabatanInput {
  name?: string;
  description?: string;
  level?: JabatanLevel;
  order?: number;
}

export interface JabatanListResponse {
  data: Jabatan[];
}

export interface JabatanResponse {
  data: Jabatan;
}

// Assignment types
export interface UserJabatanAssignment {
  _id: string;
  user: {
    _id: string;
    callsign: string;
    nama_lengkap: string;
    email: string;
    provinsi: string;
    kabupaten: string;
  };
  jabatan: {
    _id: string;
    name: string;
    description?: string;
    level: JabatanLevel;
    order: number;
  };
  province?: string;
  city?: string;
  startDate?: string;
  endDate?: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAssignmentInput {
  user: string;
  jabatan: string;
  province?: string;
  city?: string;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  notes?: string;
}

export interface UpdateAssignmentInput {
  province?: string;
  city?: string;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  notes?: string;
}

export interface AssignmentListResponse {
  data: UserJabatanAssignment[];
  total: number;
}

export interface AssignmentResponse {
  data: UserJabatanAssignment;
}

class ManagementService {
  /**
   * Fetch all jabatan with optional level filter
   * @param level - Optional level filter (nasional, province, city)
   * @returns Promise with array of jabatan
   */
  async getAllJabatan(level?: JabatanLevel): Promise<Jabatan[]> {
    try {
      const params = new URLSearchParams();
      if (level) {
        params.append("level", level);
      }

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/jabatan?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: JabatanListResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error fetching jabatan:", error);
      throw error;
    }
  }

  /**
   * Get jabatan by ID
   * @param id - Jabatan ID
   * @returns Promise with jabatan
   */
  async getJabatanById(id: string): Promise<Jabatan> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/jabatan/${id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: JabatanResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error fetching jabatan:", error);
      throw error;
    }
  }

  /**
   * Create new jabatan
   * @param input - Jabatan data
   * @returns Promise with created jabatan
   */
  async createJabatan(input: CreateJabatanInput): Promise<Jabatan> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/jabatan`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: JabatanResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error creating jabatan:", error);
      throw error;
    }
  }

  /**
   * Update jabatan by ID
   * @param id - Jabatan ID
   * @param input - Update data
   * @returns Promise with updated jabatan
   */
  async updateJabatan(id: string, input: UpdateJabatanInput): Promise<Jabatan> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/jabatan/${id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: JabatanResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error updating jabatan:", error);
      throw error;
    }
  }

  /**
   * Delete jabatan by ID
   * @param id - Jabatan ID
   * @returns Promise with deleted jabatan
   */
  async deleteJabatan(id: string): Promise<Jabatan> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/jabatan/${id}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: JabatanResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error deleting jabatan:", error);
      throw error;
    }
  }

  /**
   * Get all assignments with optional filters
   * @param filters - Optional filters (user, jabatan, province, city, level, isActive)
   * @returns Promise with array of assignments
   */
  async getAllAssignments(filters?: {
    user?: string;
    jabatan?: string;
    province?: string;
    city?: string;
    level?: JabatanLevel;
    isActive?: boolean;
  }): Promise<AssignmentListResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.user) params.append("user", filters.user);
      if (filters?.jabatan) params.append("jabatan", filters.jabatan);
      if (filters?.province) params.append("province", filters.province);
      if (filters?.city) params.append("city", filters.city);
      if (filters?.level) params.append("level", filters.level);
      if (filters?.isActive !== undefined) params.append("isActive", filters.isActive.toString());

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/assignments?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: AssignmentListResponse = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching assignments:", error);
      throw error;
    }
  }

  /**
   * Get assignment by ID
   * @param id - Assignment ID
   * @returns Promise with assignment
   */
  async getAssignmentById(id: string): Promise<UserJabatanAssignment> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/assignments/${id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: AssignmentResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error fetching assignment:", error);
      throw error;
    }
  }

  /**
   * Create new assignment
   * @param input - Assignment data
   * @returns Promise with created assignment
   */
  async createAssignment(input: CreateAssignmentInput): Promise<UserJabatanAssignment> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/assignments`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: AssignmentResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error creating assignment:", error);
      throw error;
    }
  }

  /**
   * Update assignment by ID
   * @param id - Assignment ID
   * @param input - Update data
   * @returns Promise with updated assignment
   */
  async updateAssignment(id: string, input: UpdateAssignmentInput): Promise<UserJabatanAssignment> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/assignments/${id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: AssignmentResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error updating assignment:", error);
      throw error;
    }
  }

  /**
   * Delete assignment by ID
   * @param id - Assignment ID
   * @returns Promise with deleted assignment
   */
  async deleteAssignment(id: string): Promise<void> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/assignments/${id}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error deleting assignment:", error);
      throw error;
    }
  }

}

export const managementService = new ManagementService();

